import { useState } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const location = useLocation();

  if (loading) return null;
  if (user) return <Navigate to={location.state?.from || "/"} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const r = await login(email.trim(), password);
    setBusy(false);
    if (!r.ok) setError(r.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cream-50">
      {/* Left: form */}
      <div className="flex items-center justify-center px-6 md:px-12 py-12 animate-fade-up">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <div className="h-8 w-8 rounded-md bg-forest-700 grid place-items-center">
              <span className="font-heading text-cream-50 text-sm font-semibold">3</span>
            </div>
            <div className="leading-tight">
              <div className="font-heading text-[15px] font-semibold text-forest-800">Sign-Off</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft">Charity expense approvals</div>
            </div>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl text-forest-900 mb-2 tracking-tight">
            Sign in to your account
          </h1>
          <p className="text-sm text-ink-soft mb-8">
            Submit expenses and track three-person sign-off in one calm, transparent place.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-ink-soft">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700"
                placeholder="you@charity.org"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-ink-soft">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700"
              />
            </div>

            {error && (
              <div
                data-testid="login-error-message"
                className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full bg-forest-700 hover:bg-forest-800 text-cream-50 h-11 font-medium"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="mt-8">
            <p className="text-sm text-ink-soft">
              New to Sign-Off?{" "}
              <Link to="/signup" data-testid="go-to-signup-link" className="text-forest-700 font-medium hover:underline">
                Create an organisation
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right: visual */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/7475187/pexels-photo-7475187.jpeg"
          alt="Volunteer holding green plants"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-forest-900/80 via-forest-800/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-cream-50">
          <div className="text-xs uppercase tracking-[0.2em] text-cream-200/80 mb-3">
            Built for charity governance
          </div>
          <h2 className="font-heading text-3xl xl:text-4xl leading-tight max-w-md">
            Every expense, signed off by three. Every action, recorded once — and never edited.
          </h2>
        </div>
      </div>
    </div>
  );
}
